import { IBlobEvent } from 'extendable-media-recorder';

import { DEFAULT_AUDIO_MIME_TYPE, DEFAULT_MAX_DURATION } from '../../constants/audio.ts';
import {
  AudioRecordingErrorType,
  AudioRecordingOptions,
  AudioRecordingResult,
  AudioRecordingState,
  MediaRecorderType,
} from '../../types/audio.types.ts';

import { getSentryUtil } from '../monitoring/sentry.util.ts';

import { AudioContextUtil, getAudioContextUtil } from './audio-context.util.ts';
import { AudioRecordingUploaderUtil } from './audio-recording-uploader.util.ts';
import { AudioSilenceDetectorUtil, getAudioSilenceDetectorUtil } from './audio-silence-detector.util.ts';
import { MultiAudioRecorderUtil } from './multi-audio-recorder.util.ts';

export class AudioRecordingUtil {
  private audioContextUtil: AudioContextUtil = getAudioContextUtil();
  private silenceDetector: AudioSilenceDetectorUtil = getAudioSilenceDetectorUtil();

  private multiAudioRecorder: MultiAudioRecorderUtil | null = null;
  private startTime = 0;
  private timerInterval: number | null = null;
  private stream: MediaStream | null = null;

  private state: AudioRecordingState = {
    isRecording: false,
    isHandlingRecording: false,
    duration: 0,
  };

  private options: Required<AudioRecordingOptions> = {
    maxDuration: DEFAULT_MAX_DURATION,
    silenceDetection: {
      enabled: false,
    },
    callbacks: {},
  };

  private static _instance: AudioRecordingUtil;

  static get instance(): AudioRecordingUtil {
    if (!AudioRecordingUtil._instance) {
      AudioRecordingUtil._instance = new AudioRecordingUtil();
    }
    return AudioRecordingUtil._instance;
  }

  private constructor() {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      this.handleError(AudioRecordingErrorType.NOT_SUPPORTED);
    }
  }

  get isBusy(): boolean {
    return this.state.isRecording;
  }

  async record(options?: AudioRecordingOptions): Promise<AudioRecordingResult> {
    if (this.state.isRecording || this.state.isHandlingRecording) {
      return Promise.resolve({
        error: AudioRecordingErrorType.RECORDING_IN_PROGRESS,
        success: false,
      });
    }

    return await this.handleRecording(options ?? {});
  }

  async stopRecording(): Promise<AudioRecordingResult> {
    if (!this.multiAudioRecorder || (!this.state.isRecording && !this.state.audioBlobs)) {
      return Promise.resolve({
        error: AudioRecordingErrorType.RECORDING_NOT_STARTED,
        success: false,
      });
    }

    this.multiAudioRecorder.stop();
    this.stopTimer();

    await this.multiAudioRecorder.waitForStopped();

    return new Promise((resolve) => {
      this.onRecordingComplete(resolve);
    });
  }

  getRecordingKey(): string | undefined {
    return this.state.recordingKey;
  }

  private startTimer(): void {
    this.startTime = Date.now();
    this.timerInterval = window.setInterval(() => {
      const duration = (Date.now() - this.startTime) / 1000;
      this.state.duration = duration;

      if (duration >= this.options.maxDuration) {
        this.stopRecording();
      }
    }, 100);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private handleDataAvailable(type: MediaRecorderType, event: IBlobEvent): void {
    if (event.data.size > 0) {
      this.options.callbacks?.onPartialResult?.({
        mediaRecorderType: type,
        audioBlob: event.data,
        timeStamp: event.timeStamp,
      });
    }
  }

  private handleStop(
    uploader: AudioRecordingUploaderUtil,
    resolve: (value: AudioRecordingResult) => void,
    audioBlobs: Record<MediaRecorderType, Blob>
  ): void {
    this.state.isRecording = false;
    this.state.audioBlobs = audioBlobs;

    this.cleanup();
    this.onRecordingComplete(resolve);

    if (MediaRecorderType.STORAGE in this.state.audioBlobs) {
      uploader.upload(this.state.audioBlobs[MediaRecorderType.STORAGE]);
    }
  }

  private async handleRecording(options?: AudioRecordingOptions): Promise<AudioRecordingResult> {
    try {
      const multiAudioRecorder = await this.audioContextUtil.getMultiAudioRecorder({
        mimeType: DEFAULT_AUDIO_MIME_TYPE,
      });
      this.options.maxDuration = options?.maxDuration ?? DEFAULT_MAX_DURATION;
      this.options.silenceDetection = {
        ...this.options.silenceDetection,
        ...(options?.silenceDetection ?? {}),
      };
      this.options.callbacks = options?.callbacks ?? {};

      const uploader = new AudioRecordingUploaderUtil();
      this.state.prepareUploadPromise = uploader.prepareUpload(); // Don't await so we can start recording immediately
      this.state.recordingKey = undefined;

      return new Promise((resolve) => {
        multiAudioRecorder.init({
          ondataavailable: this.handleDataAvailable.bind(this),
          onstop: this.handleStop.bind(this, uploader, resolve),
          onerror: this.handleError.bind(this, AudioRecordingErrorType.RECORDING_ERROR),
        });

        this.multiAudioRecorder = multiAudioRecorder;
        multiAudioRecorder.start(100);
        this.state.isRecording = true;
        this.state.isHandlingRecording = true;
        this.state.audioBlobs = undefined;
        this.state.error = undefined;
        this.startSilenceDetection(options);
        this.startTimer();
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'NotAllowedError') {
        this.handleError(AudioRecordingErrorType.PERMISSION_DENIED, error);
        return Promise.resolve({
          error: AudioRecordingErrorType.PERMISSION_DENIED,
          success: false,
        });
      } else {
        this.handleError(AudioRecordingErrorType.INITIALIZATION_ERROR, error as Error);
        return Promise.resolve({
          error: AudioRecordingErrorType.INITIALIZATION_ERROR,
          success: false,
        });
      }
    }
  }

  private async startSilenceDetection(options?: AudioRecordingOptions): Promise<void> {
    if (this.silenceDetector.isBusy) {
      this.handleError(AudioRecordingErrorType.SILENCE_DETECTION_IN_PROGRESS);
      return;
    }

    if (options?.silenceDetection?.enabled) {
      const stream = await this.audioContextUtil.getStream();
      this.silenceDetector.initialize(stream, {
        ...options.silenceDetection,
        onSilenceDetected: (): void => {
          console.log('onSilenceDetected');
          options?.silenceDetection?.onSilenceDetected?.();
          this.stopRecording();
        },
      });
    }
  }

  private async onRecordingComplete(resolve: (value: AudioRecordingResult) => void): Promise<void> {
    try {
      if (this.state.audioBlobs) {
        this.state.recordingKey = (await this.state.prepareUploadPromise)?.key;
        resolve({
          audioBlobs: this.state.audioBlobs,
          duration: this.state.duration,
          recordingKey: this.state.recordingKey ?? '',
          success: true,
        });
      } else {
        resolve({
          error: AudioRecordingErrorType.RECORDING_ERROR,
          success: false,
        });
      }
    } finally {
      this.state.isHandlingRecording = false;
    }
  }

  private cleanup(): void {
    this.stopTimer();
    this.silenceDetector.stop();
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.options.callbacks = {};
  }

  private handleError(type: AudioRecordingErrorType, error?: Error): void {
    const err = error ?? new Error(type);
    getSentryUtil().trackError(err, { type });
    this.state.error = err;
    this.stopRecording();
  }
}

export const getAudioRecordingUtil = (): AudioRecordingUtil => AudioRecordingUtil.instance;
